import React, { useState } from "react";
import ReportCount from "./ReportCount";
import updateAOV from "../../utils/updateAOV";
import updateConversionRate from "../../utils/updateConversionRate";
import readReportCount from "../../utils/readReportCount";
import { reportingGoals } from "../../src/reportingGoals";

const trackedCount = (id) => readReportCount(document.getElementById(id)) > 0;

const cssId = (id) =>
  /^[A-Za-z_][A-Za-z0-9_-]*$/.test(String(id))
    ? `#${id}`
    : `[id="${String(id).replace(/"/g, '\\"')}"]`;

const CountClause = ({ id, path, payload, label }) => {
  const [visible, setVisible] = useState(false);

  return (
    <span hidden={!visible}>
      {" "}
      <ReportCount
        inline={true}
        id={id}
        path={path}
        payload={payload}
        callback={() => {
          setVisible(trackedCount(id));
        }}
      />{" "}
      {label}.
    </span>
  );
};

const OrdersClause = ({ idBase, path, currency, scope }) => {
  const ordersId = `${idBase}-orders`;
  const revenueId = `${idBase}-revenue`;
  const aovId = `${idBase}-aov`;
  const [visible, setVisible] = useState(false);

  const refresh = () => {
    const hasOrders = trackedCount(ordersId);
    setVisible(hasOrders);
    if (hasOrders) {
      updateAOV(`#${ordersId}`, `#${revenueId}`, `#${aovId}`);
    }
  };

  return (
    <span hidden={!visible}>
      {" "}
      <ReportCount
        inline={true}
        id={ordersId}
        path={path}
        payload={{
          report_type: "count",
          report: { ...scope, event_type: "Order" },
        }}
        callback={refresh}
      />{" "}
      orders with an average value of {currency}
      <span id={aovId}>0.00</span> (Total:{" "}
      <ReportCount
        inline={true}
        id={revenueId}
        path={path}
        payload={{
          report_type: "revenue",
          report: { ...scope, event_type: "Order" },
        }}
        callback={refresh}
        currency={currency}
      />
      ).
    </span>
  );
};

const publishIfMounted = (id, setVisible) => {
  if (!document.getElementById(id)) return;
  setVisible(trackedCount(id));
};

const DetailsCount = ({ id, path, payload, label }) => {
  const [visible, setVisible] = useState(false);

  return (
    <span hidden={!visible}>
      {" - "}
      <strong>
        <ReportCount
          inline={true}
          id={id}
          path={path}
          payload={payload}
          callback={() => publishIfMounted(id, setVisible)}
        />
      </strong>{" "}
      {label}
    </span>
  );
};

const DetailsOrders = ({ idBase, path, currency, scope, selectionsId }) => {
  const ordersId = `${idBase}-orders`;
  const revenueId = `${idBase}-revenue`;
  const aovId = `${idBase}-aov`;
  const rateId = `${idBase}-order-rate`;
  const [visible, setVisible] = useState(false);

  const refresh = () => {
    const ordersSelector = cssId(ordersId);
    updateAOV(ordersSelector, cssId(revenueId), cssId(aovId));
    if (selectionsId) {
      updateConversionRate(cssId(selectionsId), ordersSelector, cssId(rateId));
    }
    publishIfMounted(ordersId, setVisible);
  };

  return (
    <span className="funnel-breakdown-revenue-details" hidden={!visible}>
      {" - "}
      <strong>
        <ReportCount
          inline={true}
          id={ordersId}
          path={path}
          payload={{
            report_type: "count",
            report: { ...scope, event_type: "Order" },
          }}
          callback={refresh}
        />
      </strong>{" "}
      orders (<span id={rateId}>0.00</span>){" - "}
      <strong>
        <ReportCount
          inline={true}
          id={revenueId}
          path={path}
          payload={{
            report_type: "revenue",
            report: { ...scope, event_type: "Order" },
          }}
          callback={refresh}
          currency={currency}
        />
      </strong>{" "}
      revenue
      {" - "}
      <strong>
        {currency}
        <span id={aovId}>0.00</span>
      </strong>{" "}
      AOV
    </span>
  );
};

const FunnelTrackedMetrics = (props) => {
  if (props.gateReports) return null;

  const goals = reportingGoals(props.reportingGoals);
  const idBase = props.idBase;
  const path = props.path;

  if (props.layout === "details") {
    return (
      <React.Fragment>
        {goals.checkouts && (
          <DetailsOrders
            idBase={idBase}
            path={path}
            currency={props.currency}
            scope={props.report}
            selectionsId={props.selectionsId}
          />
        )}
        {goals.bookings && (
          <DetailsCount
            id={`${idBase}-bookings`}
            path={path}
            label="bookings"
            payload={{
              report_type: "distinct_count",
              report: { ...props.report, event_type: "Booking" },
            }}
          />
        )}
        {goals.custom && (
          <DetailsCount
            id={`${idBase}-custom-goals`}
            path={path}
            label={goals.customGoalLabel}
            payload={{
              report_type: "distinct_count",
              report: { ...props.report, event_type: "Custom Goal" },
            }}
          />
        )}
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      {goals.checkouts && (
        <OrdersClause
          idBase={idBase}
          path={path}
          currency={props.currency}
          scope={props.report}
        />
      )}
      {goals.bookings && (
        <CountClause
          id={`${idBase}-bookings`}
          path={path}
          label="bookings"
          payload={{
            report_type: "distinct_count",
            report: { ...props.report, event_type: "Booking" },
          }}
        />
      )}
      {goals.custom && (
        <CountClause
          id={`${idBase}-custom-goals`}
          path={path}
          label={goals.customGoalLabel}
          payload={{
            report_type: "distinct_count",
            report: { ...props.report, event_type: "Custom Goal" },
          }}
        />
      )}
    </React.Fragment>
  );
};

export default FunnelTrackedMetrics;
